import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageSquare } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import Logo from "@/components/Logo";
import { COMPANY } from "@/lib/site";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/services", label: "Services", testid: "nav-services" },
  { to: "/projects", label: "Projects", testid: "nav-projects" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-max flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="group"
        >
          <Logo variant="full" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `px-4 py-2 font-heading text-sm tracking-[0.2em] uppercase transition-colors ${
                  isActive
                    ? "text-[#FF5A00]"
                    : "text-zinc-300 hover:text-zinc-50"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-zinc-300 hover:text-[#FF5A00] transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="font-heading text-sm tracking-wider">
              {COMPANY.phone}
            </span>
          </a>
          <a
            href={COMPANY.smsHref}
            data-testid="nav-text"
            aria-label="Text us"
            title="Text us"
            className="flex items-center gap-2 text-zinc-300 hover:text-[#FF5A00] transition-colors border-l border-zinc-800 pl-4"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="font-heading text-xs tracking-[0.2em] uppercase">
              Text
            </span>
          </a>
          <a
            href={COMPANY.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp"
            aria-label="Chat on WhatsApp"
            title="Chat on WhatsApp"
            className="flex items-center gap-2 text-zinc-300 hover:text-[#25D366] transition-colors"
          >
            <WhatsAppIcon className="w-4 h-4" />
            <span className="font-heading text-xs tracking-[0.2em] uppercase">
              WhatsApp
            </span>
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-quote"
            className="btn-primary"
          >
            Request Quote
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="lg:hidden text-zinc-100 p-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-zinc-950 border-t border-zinc-800"
        >
          <div className="container-max py-6 flex flex-col gap-2">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `py-3 font-heading text-base tracking-[0.2em] uppercase border-b border-zinc-800 ${
                    isActive ? "text-[#FF5A00]" : "text-zinc-200"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-quote-mobile"
              className="btn-primary mt-4 justify-center"
            >
              Request Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
